import { Fragment } from "react";
import { diffWords, markProtected, type DiffSegment } from "../lib/diff";

/**
 * DOCUMENT DIFF — ONE component, serving BOTH /new (editable After) and /review (read-only
 * After). These were two divergent implementations of the single most important
 * comprehension task in the product: a reviewer deciding whether a machine may alter a
 * document going to a funder. The standard is a diff a lawyer would sign.
 *
 * WHAT IT GUARANTEES
 *  - WORD-LEVEL marking (lib/diff.ts, LCS, no dependency). Unchanged text stays --ink and
 *    still reads as a sentence, so the eye lands only on what actually moved.
 *  - COLOUR IS NEVER THE SOLE CHANNEL. <del> carries a strikethrough and a "−" mark; <ins>
 *    carries a "+" mark and NO underline (an underline reads as a link). In sunlight, on a
 *    monochrome screen, or to a colour-blind reviewer, the marks still carry the meaning.
 *  - PROTECTED TOKENS — numbers, dates, currencies, citations, URLs — render as SEALED CHIPS
 *    on --surface-locked with a hairline amber outline. The product's core promise is that
 *    the machine never silently alters these; the operator can now SEE it being kept.
 *  - NEVER TRUNCATED. The After block renders in full, at uncapped height. If the text is
 *    long, the card is long. A decision surface may scroll the page, but it may never hide
 *    the thing being decided. Gate-enforced: scrollHeight <= clientHeight.
 *  - --serif at --measure. The document finally reads as a document.
 *
 * PHONE: stacked, Before over After, full width. Side by side at 390px yields two ~150px
 * columns and is unreadable. DESKTOP (>=1024): two columns, each at its own measure.
 */

/** Split a run of text into protected / unprotected pieces and seal the protected ones. */
function ProtectedText({ value }: { value: string }) {
  const pieces = markProtected(value);
  return (
    <>
      {pieces.map((piece, index) =>
        piece.protected ? (
          <mark className="doc-protected" title="Protected fact — never altered" key={index}>
            {piece.value}
          </mark>
        ) : (
          <Fragment key={index}>{piece.value}</Fragment>
        )
      )}
    </>
  );
}

function DiffRun({ segments, side }: { segments: DiffSegment[]; side: "before" | "after" }) {
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === "equal") {
          return <ProtectedText value={segment.value} key={index} />;
        }
        // The Before block shows what is being REMOVED. The After block shows what is being
        // ADDED. Neither shows the other side's marks, so each column reads as a whole,
        // continuous piece of prose rather than as a merge conflict.
        if (side === "before" && segment.type === "delete") {
          return (
            <del key={index}>
              <ProtectedText value={segment.value} />
            </del>
          );
        }
        if (side === "after" && segment.type === "insert") {
          return (
            <ins key={index}>
              <ProtectedText value={segment.value} />
            </ins>
          );
        }
        return null;
      })}
    </>
  );
}

export function DocumentDiff({
  before,
  after,
  editable,
  onAfterChange,
  editLabel,
  editTestId,
  beforeLabel = "Before",
  afterLabel = "After"
}: {
  before: string;
  after: string;
  /** /new lets the operator rewrite the proposed copy before the document is created. */
  editable?: boolean;
  onAfterChange?: (value: string) => void;
  editLabel?: string;
  editTestId?: string;
  beforeLabel?: string;
  afterLabel?: string;
}) {
  const segments = diffWords(before, after);
  const changed = segments.some((segment) => segment.type !== "equal");

  return (
    <div className="doc-diff" data-testid="change-diff" data-changed={changed ? "true" : "false"}>
      <div className="doc-diff-side" data-side="before">
        <span className="doc-diff-label">{beforeLabel}</span>
        <p className="doc-diff-body" data-testid="diff-before">
          <DiffRun segments={segments} side="before" />
        </p>
      </div>
      <div className="doc-diff-side" data-side="after">
        <span className="doc-diff-label">{afterLabel}</span>
        <p className="doc-diff-body" data-testid="diff-after">
          <DiffRun segments={segments} side="after" />
        </p>
        {editable ? (
          <label className="doc-diff-edit">
            <span>{editLabel || "Rewrite the proposed text"}</span>
            <textarea
              aria-label={editLabel || "Rewrite the proposed text"}
              data-testid={editTestId}
              value={after}
              onChange={(event) => onAfterChange?.(event.target.value)}
              rows={4}
            />
          </label>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The one-line legend. It is rendered ONCE per decision surface, not once per card — saying
 * a thing three times on one screen is exactly the childishness this rebuild removes.
 */
export function DiffLegend() {
  return (
    <p className="doc-diff-legend">
      <span className="doc-diff-legend-del">− removed</span>
      <span className="doc-diff-legend-ins">+ added</span>
      <span className="doc-diff-legend-protected">Protected facts are never altered</span>
    </p>
  );
}
